import type { FC } from 'react';
import React from 'react';
import { useApolloClient } from '@apollo/react-hooks';

import { SpaceStarTextButton } from '@confluence/action-buttons/entry-points/spaceStar';

import {
	FavouriteSpaceQuery,
	SpaceOverviewCurrentSpaceQuery,
	SpaceOverviewStarredSpacesQuery,
} from './FavouriteSpaceQuery.graphql';

export type AnalyticsOptions = {
	source: string;
	containerId?: string;
	attributes?: Record<string, any>;
};

interface SpaceFavouriteProps {
	spaceKey: string;
	contentId: string;
	isFavourited: boolean | undefined;
	spaceId: string | undefined;
	analytics?: AnalyticsOptions;
}

export const SpaceFavourite: FC<SpaceFavouriteProps> = ({ ...props }) => {
	const { spaceKey, isFavourited, spaceId, analytics } = props;
	const hasStatusLoaded = typeof isFavourited === 'boolean';
	const client = useApolloClient();

	const modifyStarCache = (spaceId, star) => {
		client.writeQuery({
			query: FavouriteSpaceQuery,
			variables: { spaceKey },
			data: {
				space: {
					currentUser: {
						isFavourited: star,
						__typename: 'SpaceUserMetadata',
					},
					id: spaceId,
					__typename: 'Space',
				},
			},
		});

		// update the top nav spaces dropdown
		try {
			const currentSpace = client.readQuery({
				query: SpaceOverviewCurrentSpaceQuery,
				variables: { selectedSpaceKey: spaceKey },
			}).space;

			let starredSpaces = client.readQuery({
				query: SpaceOverviewStarredSpacesQuery,
			}).spaces.nodes;

			if (star) {
				if (!starredSpaces.find((space) => space.id === currentSpace.id)) {
					starredSpaces.push(currentSpace);
				}
			} else {
				starredSpaces = starredSpaces.filter((space) => space.id !== currentSpace.id);
			}

			let newStarredSpaces = starredSpaces.sort((a, b) => a.name.localeCompare(b.name));
			newStarredSpaces = {
				spaces: { nodes: newStarredSpaces, __typename: 'PaginatedSpaceList' },
			};

			client.writeQuery({
				query: SpaceOverviewStarredSpacesQuery,
				data: newStarredSpaces,
			});
		} catch (e) {}
	};

	/*
	 * If the query is loading or the space is favorited, show the unfavorite button as a default placeholder (matches current behavior on prod).
	 * Otherwise, render favorite button
	 */
	return (
		<SpaceStarTextButton
			spaceId={spaceId!}
			spaceKey={spaceKey}
			isStarred={isFavourited || !hasStatusLoaded}
			analytics={analytics}
			modifyStarCache={modifyStarCache}
			dataTestId="space-star-text-button"
		/>
	);
};
